<template>
  <div ref="scrollerRef" class="flex h-8 min-w-0 grow justify-end gap-2">
    <div
      v-for="(action, index) in actions"
      :key="index"
      ref="itemRefs"
      class="flex items-center"
    >
      <TrayAction
        :icon="action.icon"
        :label="action.label"
        :description="action.description"
        :hotkey="action.hotkey"
        :disabled="action.disabled"
        :to="action.to"
        @click="action.onClick"
      />
    </div>

    <Tooltip.Hint v-if="overflown" text="More actions">
      <Menu.Root>
        <Menu.Trigger>
          <Tooltip.Hint text="More actions">
            <button
              type="button"
              aria-label="More actions"
              class="grid size-8 shrink-0 place-items-center rounded-md text-gray-700 transition hover:bg-gray-300 active:bg-gray-300 aria-expanded:bg-gray-300 dark:text-muted-foreground dark:hover:bg-muted dark:active:bg-muted dark:aria-expanded:bg-muted"
            >
              <EllipsisHorizontalIcon class="size-4" />
            </button>
          </Tooltip.Hint>
        </Menu.Trigger>

        <Menu.Container>
          <template v-for="(item, index) of overflown" :key="index">
            <Menu.RouteItem
              v-if="item.to"
              :disabled="item.disabled"
              :to="item.to"
              :icon="item.icon"
              :label="item.label"
              @click="item.onClick"
            />
            <Menu.Item
              v-else
              :disabled="item.disabled"
              :icon="item.icon"
              :label="item.label"
              @click="item.onClick"
            />
          </template>
        </Menu.Container>
      </Menu.Root>
    </Tooltip.Hint>
  </div>
</template>

<script setup lang="ts">
import { onWatcherCleanup, ref, shallowRef, watch } from "vue";

import Menu from "@/design_system/Menu";
import Tooltip from "@/design_system/Tooltip";
import EllipsisHorizontalIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";

import TrayAction from "./Action.vue";
import type { TrayActionItem } from "./internal/tray-types";

const props = defineProps<{
  actions: TrayActionItem[];
}>();

const OVERFLOW_BUTTON_WIDTH = 32;
const SPACING_GAP = 8;

const overflown = shallowRef<TrayActionItem[]>();

const scrollerRef = ref<HTMLElement>();
const itemRefs = ref<HTMLElement[]>([]);

const measure = () => {
  const totalWidth = scrollerRef.value!.clientWidth;
  const nodes = itemRefs.value;

  const length = nodes.length;

  let idx = 0;
  let accumulatedWidth = OVERFLOW_BUTTON_WIDTH;

  // Unset all `display: none` that we've set in the previous run
  for (; idx < length; idx++) {
    const node = nodes[idx];
    node.style.display = "";
  }

  // Measure the available width
  for (idx = 0; idx < length; idx++) {
    const node = nodes[idx];
    if (!node.isConnected) {
      return;
    }

    const width = SPACING_GAP + node.clientWidth;

    if (accumulatedWidth + width > totalWidth) {
      break;
    }

    accumulatedWidth += width;
  }

  // Hide the rest
  if (idx < length) {
    overflown.value = props.actions.slice(idx);
  } else {
    overflown.value = undefined;
  }

  for (; idx < length; idx++) {
    const node = nodes[idx];
    node.style.display = "none";
  }
};

let rafId: number | undefined;
const enqueueMeasure = () => {
  if (rafId !== undefined) {
    cancelAnimationFrame(rafId);
  }

  rafId = requestAnimationFrame(measure);
};

watch(scrollerRef, (scroller) => {
  if (!scroller) {
    return;
  }

  let storedWidth = 0;
  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    const width = entry.contentRect.width;

    if (width === storedWidth) {
      return;
    }

    storedWidth = width;
    enqueueMeasure();
  });

  onWatcherCleanup(() => observer.disconnect());
  onWatcherCleanup(watch(itemRefs, enqueueMeasure));

  observer.observe(scroller);
});
</script>
